import React, { useEffect } from 'react';
import App, { AppProps } from 'next/app';
import Head from 'next/head';
import Router from 'next/router';
import { Provider } from 'react-redux';

import NProgress from 'nprogress';
import { initAxiosInstance } from '@/services/http';
import { getTranslation } from '@/services/locale';
import { wrapper } from '@/store';
import { ConfigProvider } from 'antd';
import '@/styles/reset.scss';
import '@/styles/rich-text.css';
import { changeLanguage } from '@/store/slices/homePageSlice';
import 'swiper/css';
import 'swiper/css/effect-fade';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import getConfig from 'next/config';
import Script from 'next/script';
import { filterSvgPath } from '@/utils/dataFormat';
import dynamic from 'next/dynamic';
import { AppRoute } from '@/type/app';
import { AppProvider } from '@/hooks/AppContext';
import { getHeaderAndFooter } from '@/services/headerAndFooter';
import { getBlogFeatureConfig, getFeatureConfig } from '@/services/contentstack';
import { HEADER_FOOTER_CONTENT_TYPE } from '@/constants/headerAndFooter';
import { headerAndFooterIncludeReference } from '@/constants/homePage';
import { MetaData } from '@/components/PageMeta/PageMeta.types';
import ErrorBoundary from '@/components/ErrorBoundary';

import '@/styles/variables.css';

const NewLayout = dynamic(() => import('@/Layout/Layout'));
const PageMeta = dynamic(() => import('@/components/PageMeta'));

Router.events.on('routeChangeStart', () => NProgress.start());
Router.events.on('routeChangeComplete', () => NProgress.done());
Router.events.on('routeChangeError', () => NProgress.done());

const useInterceptNextDataHref = ({ router, namespace }: { router: any; namespace: string }) => {
  useEffect(() => {
    if (router.pageLoader?.getDataHref) {
      const originalGetDataHref = router.pageLoader.getDataHref;
      router.pageLoader.getDataHref = function (...args: any[]) {
        const r = originalGetDataHref.call(router.pageLoader, ...args);
        return r && r.startsWith('/_next/data') && namespace ? `/${namespace}${r}` : r;
      };
    }
  }, [router, namespace]);
};

const MyApp = ({ Component, ...rest }: Omit<AppProps & AppRoute, 'pageProps'>) => {
  const { router, headerFooter } = rest;
  useInterceptNextDataHref({
    router,
    namespace: 'blog'
  });
  const { store, props } = wrapper.useWrappedStore(rest);
  const {
    pageProps,
    pathName,
    currentUrl,
    webUrl,
    language,
    langDictionary,
    // topNavContentData,
    featureConfigData,
    blogFeatureConfigData
  } = props;

  const { page, categoryPage, popUpBannerData, promotionBannerData, homepage } = pageProps;

  useEffect(() => {
    document.documentElement.className = 'notranslate';
    document.documentElement.translate = false;
    language.origin === 'en'
      ? (document.documentElement.lang = 'en')
      : (document.documentElement.lang = 'zh');
    document.documentElement.className = 'notranslate';
    document.documentElement.translate = false;
    language.origin === 'en'
      ? (document.documentElement.lang = 'en')
      : (document.documentElement.lang = 'zh');
  }, [language.origin]);

  useEffect(() => {
    if (window.self !== window.parent && window.top) {
      window.top.location.replace(window.self.location.href);
    }
  }, []);

  const MetaData: MetaData = {
    page: page,
    pageTitle: page?.title ?? 'FWD BLOG',
    settingTitle: page?.webpage_settings?.page_title || 'FWD BLOG',
    seo: page?.webpage_settings?.seo || {},
    openGraph: page?.webpage_settings?.open_graph || {},
    blogSnippet: page?.blog_snippet || {},
    wikiCategorySetUpData: page?.wiki_category_setup || {},
    profilePicture: page?.profile_picture || {},
    currentUrl: currentUrl,
    language: language.origin,
    languages: page?.languages,
    webUrl: webUrl,
    categoryPage: categoryPage,
    pathName
  };

  return (
    <>
      <ErrorBoundary>
        <Head>
          <meta name="viewport" content="width=device-width,initial-scale=1,minimum-scale=1,user-scalable=no" />
        </Head>
        <PageMeta {...MetaData} />
        <AppProvider
          langDictionary={langDictionary}
          headerFooter={headerFooter}
          featureConfigData={featureConfigData}
          blogFeatureConfigData={blogFeatureConfigData}
          page={page}
          language={language}
        >
          <Provider store={store}>
            <ConfigProvider theme={{ token: { colorPrimary: '#E87722' } }}>
              <NewLayout
                // topNavContentData={topNavContentData}
                homepage={homepage}
                headerFooter={headerFooter}
                popUpBannerData={popUpBannerData}
                promotionBannerData={promotionBannerData}
                initialState={rest?.initialState}
                pathName={pathName}
                page={page}
              >
                <Component {...pageProps} />
              </NewLayout>
            </ConfigProvider>
          </Provider>
        </AppProvider>
      </ErrorBoundary>
    </>
  );
};

MyApp.getInitialProps = wrapper.getInitialAppProps(store => async (appContext: any) => {
  const startTime = new Date().getTime();
  const { req, asPath } = appContext.ctx;

  const { publicRuntimeConfig } = getConfig();
  const webUrl = publicRuntimeConfig.APP_WEB_URL;
  const currentUrl = `${webUrl}${req?.url}`;

  const appProps = await App.getInitialProps(appContext);

  const lang =
    appContext.ctx.query.language === 'en' || asPath?.includes('/en/') ? 'en-us' : 'zh-hk';
  const headerAndFooterData = await getHeaderAndFooter(
    HEADER_FOOTER_CONTENT_TYPE,
    headerAndFooterIncludeReference,
    lang
  );

  const featureConfigRes = await getFeatureConfig();

  const blogFeatureConfigRes = await getBlogFeatureConfig();

  const headerFooter =
    Array.isArray(headerAndFooterData?.data) && Array.isArray(headerAndFooterData?.data[0])
      ? headerAndFooterData?.data[0][0]
      : {};

  const featureConfigData =
    Array.isArray(featureConfigRes?.data) && Array.isArray(featureConfigRes?.data[0])
      ? featureConfigRes?.data[0][0]
      : {};

  const blogFeatureConfigData = blogFeatureConfigRes?.data?.features || [];

  let language = {
    label: 'zh-hk',
    value: 'en',
    origin: 'zh'
  };
  const isSvg = filterSvgPath(asPath);
  if (!isSvg && asPath) {
    language =
      appContext.ctx.query.language === 'en' || asPath?.includes('/en/')
        ? {
            label: 'en-us',
            value: 'zh',
            origin: 'en'
          }
        : {
            label: 'zh-hk',
            value: 'en',
            origin: 'zh'
          };
    const { categoryPage, headerAndFooter } = store.getState();
    store.dispatch(changeLanguage(language));
    initAxiosInstance(store.dispatch, language.label);
  }

  const { default: langDictionary = {} } = await getTranslation(language.origin);

  const requestTime = new Date().getTime();

  return {
    ...appProps,
    pathName: appContext.ctx.pathname,
    isServerSideProps: false,
    webUrl,
    currentUrl,
    language,
    startTime,
    requestTime,
    langDictionary,
    headerFooter,
    featureConfigData,
    blogFeatureConfigData
  };
});

export default MyApp;
